import React, { useRef, useState, useEffect, useCallback } from 'react';
import { SoundOutlined, SoundFilled } from '@ant-design/icons';
import { SeriesProps } from '../../types/series';
import { useARCallbacks } from '../../hooks/useARCallbacks';
import { useARScene } from '../../hooks/useARScene';
import { ARScene, ARImageTarget } from '../../components/ARScene';
import { getPaintingDate, patchPaintingDate } from '../../api/api';
import './BirdsPainting.css';
import { Modal } from '../../components/Modal';
import { ARScanner } from '../../components/ARScanner';
import { getDaysTogetherText } from '../../utils/date';
import { IconButton } from '../../components/IconButton';
import { useMusicPlayer } from '../../hooks/audio/useMusicPlayer';

const TARGET_JSON = '/paintings/birds/targets/birds-target.json';

export const BirdsPainting: React.FC<SeriesProps> = ({ id }) => {
    const sceneRef = useRef<HTMLElement>(null);
    const { engine, ready, targetName, mindTargetUrl } = useARScene({
        targetJsonUrl: TARGET_JSON,
        sceneRef,
    });
    const [date, setDate] = useState<string | null>(null);
    const dateRef = useRef<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTargetFound, setIsTargetFound] = useState(false);
    const [isArReady, setIsArReady] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const autoModalShown = useRef(false);
    const autoModalTimeout = useRef<NodeJS.Timeout | null>(null);
    const { isPlaying, play, pause, toggle, cleanup, getMusicEnabled } = useMusicPlayer('/paintings/birds/music/music-birds.mp3');
    const [musicStarted, setMusicStarted] = useState(false);

    const updateDate = useCallback((newDate: string | null) => {
        setDate(newDate);
        dateRef.current = newDate;
    }, []);

    const updateARText = useCallback((value: string | null) => {
        if (sceneRef.current) {
            const textTitle = sceneRef.current.querySelector('#text_title');
            if (textTitle) {
                textTitle.setAttribute('value', getDaysTogetherText(value));
            }
        }
    }, []);

    const handleSaveDate = useCallback(async (newDate: string | null) => {
        try {
            const data = await patchPaintingDate(id, newDate);
            updateDate(data.date);
            updateARText(data.date);
        } catch (error) {
            console.error('Error updating painting date:', error);
        }
    }, [id, updateDate, updateARText]);

    useEffect(() => {
        const fetchPaintingDate = async () => {
            try {
                const data = await getPaintingDate(id);
                updateDate(data.date);
            } catch (error) {
                console.error('Error fetching painting date:', error);
            }
        };
        fetchPaintingDate();
    }, [id, updateDate]);

    useEffect(() => cleanup, [cleanup]);

    useARCallbacks({
        sceneRef,
        targetName,
        engine,
        enabled: ready,
        callbacks: {
            arReady: () => setIsArReady(true),
            arLoaded: () => {},
            arError: (error: Error) => console.error('[BirdsCard] AR error:', error),
            targetFound: () => {
                setIsTargetFound(true);
                setShowContent(true);
                if (!musicStarted && getMusicEnabled()) {
                    play();
                    setMusicStarted(true);
                }
                if (dateRef.current === null && !autoModalShown.current) {
                    autoModalTimeout.current = setTimeout(() => {
                        setIsModalOpen(true);
                        autoModalShown.current = true;
                    }, 3000);
                }
                updateARText(dateRef.current);
            },
            targetLost: () => {
                setIsTargetFound(false);
                setShowContent(false);
                pause();
                if (autoModalTimeout.current) {
                    clearTimeout(autoModalTimeout.current);
                    autoModalTimeout.current = null;
                }
            },
        },
    });

    return (
        <div className="augmented_layout">
            <ARScanner isVisible={!isTargetFound && isArReady} />
            <IconButton
                type="date"
                onClick={() => setIsModalOpen(true)}
                className={isTargetFound ? 'icon-button--visible' : 'icon-button--hidden'}
            />
            {isTargetFound && (
                <IconButton
                    type={isPlaying ? 'music-on' : 'music-off'}
                    onClick={toggle}
                    className="music-icon-button"
                    icon={isPlaying ? <SoundFilled className="icon-button__icon" /> : <SoundOutlined className="icon-button__icon" />}
                />
            )}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                mode="date"
                value={date}
                onSave={handleSaveDate}
            />
            <ARScene
                engine={engine}
                ready={ready}
                mindTargetUrl={mindTargetUrl}
                sceneRef={sceneRef}
            >
                <a-assets>
                    <img
                        id="resource_flowers"
                        src="/paintings/birds/images/birds-flowers.webp"
                        alt="Resource flowers"
                    />
                    <a-asset-item
                        id="adleryFont"
                        src="/paintings/shared/fonts/adlery.woff"
                    />
                </a-assets>
                <a-camera
                    position="0 0 0"
                    look-controls="enabled: false"
                />
                <ARImageTarget engine={engine} targetName={targetName}>
                    <a-troika-text
                        id="text_title"
                        value=""
                        color="#FFFFFF"
                        position={`0 -0.1 0`}
                        font-size="0.12"
                        baseline="top"
                        align="center"
                        font="/paintings/shared/fonts/adlery.woff"
                        animation__fade={`property: opacity; to: ${showContent ? 1 : 0}; dur: 500; easing: easeInOutSine`}
                    />
                    <a-image
                        id="image_flowers"
                        src="#resource_flowers"
                        width="1.4"
                        height="1.4"
                        position={`0 0 0.001`}
                        animation__fade={`property: opacity; to: ${showContent ? 1 : 0}; dur: 500; easing: easeInOutSine`}
                    />
                </ARImageTarget>
            </ARScene>
        </div>
    );
};
